/* Master Custom Build by CHRISPSDesign · © 2026 CHRISPSDesign · https://chrisps.ca · Not for redistribution. */
/**
 * CHRISPSDesign Core · chrisps-core.js
 * ---------------------------------------------------------------------------
 * The only script loaded on every page (deferred). Everything else is loaded
 * by the section that needs it. No frameworks, no dependencies.
 *
 * Exposes a single namespace: window.ChrispsCore
 *   .config            routes, strings and settings (from snippets/chrisps-config)
 *   .cart              Ajax Cart API helpers + bundled section rendering
 *   .dialog            accessible <dialog> open/close with focus return
 *   .storage           safe localStorage wrapper
 *   .loadScript(url)   load a feature script once
 *   .animate(root)     (re)initialise the animation engine inside root
 *
 * Custom elements defined here:
 *   <chrisps-cart-drawer> <chrisps-cart-items> <chrisps-free-shipping>
 *   <chrisps-wishlist-button> <chrisps-predictive-search> <chrisps-countdown>
 *   <chrisps-header> <chrisps-disclosure-menu>
 *
 * Events (dispatched on document):
 *   chrisps:cart:updated   detail: { cart, source }
 *   chrisps:wishlist:updated detail: { handles }
 *   chrisps:variant:change detail: { sectionId, variant }
 */
(() => {
  if (window.ChrispsCore && window.ChrispsCore.ready) return;

  const Core = (window.ChrispsCore = window.ChrispsCore || {});
  const config = (Core.config = Object.assign(
    { routes: {}, strings: {}, settings: {} },
    JSON.parse(document.getElementById('chrisps-config')?.textContent || '{}')
  ));
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ----------------------------------------------------------------------
   * Utilities
   * -------------------------------------------------------------------- */
  Core.designMode = designMode;

  Core.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  Core.fetchHTML = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(`${response.status} ${url}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  Core.formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || config.settings.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  const loadedScripts = new Map();
  Core.loadScript = (url) => {
    if (!url) return Promise.resolve();
    if (loadedScripts.has(url)) return loadedScripts.get(url);
    if (document.querySelector(`script[src="${url}"]`)) {
      const p = Promise.resolve();
      loadedScripts.set(url, p);
      return p;
    }
    const p = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = url;
      s.defer = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    loadedScripts.set(url, p);
    return p;
  };

  Core.storage = {
    get(key, fallback) {
      try {
        const v = window.localStorage.getItem(key);
        return v === null ? fallback : JSON.parse(v);
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* storage unavailable (private mode) — feature degrades silently */
      }
    },
  };

  /* ----------------------------------------------------------------------
   * Accessible dialogs & drawers (native <dialog>: focus trap + Esc)
   * -------------------------------------------------------------------- */
  Core.dialog = {
    open(dialog, trigger) {
      if (!dialog || dialog.open) return;
      dialog._chrispsTrigger = trigger || document.activeElement;
      dialog.showModal();
      document.body.classList.add('chrisps-scroll-lock');
      const focusTarget = dialog.querySelector('[autofocus]') || dialog.querySelector('button, [href], input, select, textarea');
      focusTarget?.focus();
      if (!dialog._chrispsBound) {
        dialog._chrispsBound = true;
        dialog.addEventListener('close', () => {
          document.body.classList.remove('chrisps-scroll-lock');
          dialog._chrispsTrigger?.focus?.();
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.querySelectorAll('[data-chrisps-close]').forEach((btn) => btn.addEventListener('click', () => dialog.close()));
      }
    },
    close(dialog) {
      if (dialog?.open) dialog.close();
    },
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-chrisps-open]');
    if (!opener) return;
    const target = opener.dataset.chrispsOpen;
    // "Prefix-*" opens the first matching dialog (e.g. the header's search from the bottom bar).
    const dialog = target.endsWith('*') ? document.querySelector(`dialog[id^="${CSS.escape(target.slice(0, -1))}"]`) : document.getElementById(target);
    if (dialog && dialog.tagName === 'DIALOG') {
      event.preventDefault();
      Core.dialog.open(dialog, opener);
    }
  });

  /* ----------------------------------------------------------------------
   * Animation engine
   * -------------------------------------------------------------------- */
  let observer;
  Core.animate = (root = document) => {
    const nodes = root.querySelectorAll('[data-chrisps-animate]:not(.is-inview)');
    nodes.forEach((node) => {
      const groups = node.querySelectorAll('.chrisps-animate-group');
      if (!node.hasAttribute('data-chrisps-animate-no-stagger')) {
        groups.forEach((group) => {
          [...group.children]
            .filter((child) => child.classList.contains('chrisps-animate-target'))
            .forEach((child, i) => {
              if (!child.style.getPropertyValue('--chrisps-animate-index')) child.style.setProperty('--chrisps-animate-index', Math.min(i, 12));
            });
        });
      }
      if (!groups.length) node.classList.add('chrisps-animate-self');
    });
    if (reducedMotion.matches || designMode || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-inview'));
      return;
    }
    observer =
      observer ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-inview');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    // Anything already on screen animates in immediately: above-the-fold
    // content (heroes, LCP images) must never wait for an observer callback.
    const vh = window.innerHeight;
    nodes.forEach((node) => {
      const r = node.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) requestAnimationFrame(() => node.classList.add('is-inview'));
      else observer.observe(node);
    });
  };

  /* ----------------------------------------------------------------------
   * Scheduling (client-side re-check so cached pages switch correctly)
   * -------------------------------------------------------------------- */
  Core.applySchedules = (root = document) => {
    if (designMode) return;
    const now = Date.now() / 1000;
    root.querySelectorAll('[data-chrisps-schedule-start], [data-chrisps-schedule-end]').forEach((el) => {
      const toTs = (v) => (/^\d{9,}$/.test(v || '') ? Number(v) : NaN);
      const start = toTs(el.dataset.chrispsScheduleStart);
      const end = toTs(el.dataset.chrispsScheduleEnd);
      const started = Number.isNaN(start) || start <= now;
      const ended = !Number.isNaN(end) && end <= now;
      el.hidden = !started || ended;
    });
  };

  /* ----------------------------------------------------------------------
   * Cart (Ajax API + bundled section rendering)
   * -------------------------------------------------------------------- */
  const cartSections = () => {
    const ids = ['chrisps-cart-count'];
    if (document.getElementById('shopify-section-cart-drawer')) ids.push('cart-drawer');
    document.querySelectorAll('[data-chrisps-cart-section]').forEach((el) => ids.push(el.dataset.chrispsCartSection));
    return [...new Set(ids)];
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (id === 'chrisps-cart-count') {
        const count = doc.querySelector('[data-chrisps-cart-count]')?.dataset.chrispsCartCount ?? '0';
        document.querySelectorAll('[data-chrisps-cart-count]').forEach((bubble) => {
          bubble.dataset.count = count;
          bubble.textContent = count === '0' ? '' : count;
        });
        return;
      }
      document.querySelectorAll(`[data-chrisps-cart-region="${id}"]`).forEach((target) => {
        const source = doc.querySelector(`[data-chrisps-cart-region="${id}"]`);
        if (source) target.innerHTML = source.innerHTML;
      });
    });
  };

  const cartRequest = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || config.strings.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  Core.cart = {
    async add(formData, source) {
      formData.append('sections', cartSections().join(','));
      formData.append('sections_url', window.location.pathname);
      const data = await cartRequest(`${config.routes.cart_add_url}`, formData);
      renderCartSections(data.sections);
      document.dispatchEvent(new CustomEvent('chrisps:cart:updated', { detail: { cart: data, source, action: 'add' } }));
      Core.cart.afterAdd();
      return data;
    },
    async change(line, quantity, source) {
      const data = await cartRequest(`${config.routes.cart_change_url}`, {
        line,
        quantity,
        sections: cartSections(),
        sections_url: window.location.pathname,
      });
      renderCartSections(data.sections);
      document.dispatchEvent(new CustomEvent('chrisps:cart:updated', { detail: { cart: data, source, action: 'change' } }));
      return data;
    },
    async update(attributes) {
      return cartRequest(`${config.routes.cart_update_url}`, attributes);
    },
    async refresh() {
      const response = await fetch(`${config.routes.root_url}?sections=${cartSections().join(',')}`);
      renderCartSections(await response.json());
    },
    afterAdd() {
      const drawer = document.querySelector('chrisps-cart-drawer');
      if (config.settings.cartType === 'drawer' && drawer) {
        drawer.open();
      } else if (config.settings.cartType === 'page') {
        window.location.href = config.routes.cart_url;
      }
    },
  };

  /* Generic Ajax add-to-cart (product page, quick shop, sticky bar, cart
     cross-sells, frequently bought together).
     A form qualifies when it sits inside <chrisps-product-form> (markup the
     theme fully controls) or carries data-chrisps-product-form. The wrapper
     is the primary marker so detection never depends on how Shopify's
     {% form %} tag serialises custom attributes. Only /cart/add forms are
     intercepted; anything else submits natively. */
  const productFormFor = (target) => {
    const form = target instanceof HTMLFormElement ? target : target?.closest?.('form');
    if (!form) return null;
    const marked = form.hasAttribute('data-chrisps-product-form') || form.closest('chrisps-product-form');
    if (!marked) return null;
    const action = form.getAttribute('action') || '';
    return /\/cart\/add(\.js)?(\?|$)/.test(action) ? form : null;
  };
  Core.isProductForm = (form) => Boolean(productFormFor(form));

  document.addEventListener('submit', async (event) => {
    const form = productFormFor(event.target);
    if (!form || !config.settings.ajaxCart || event.defaultPrevented) return;
    event.preventDefault();
    const submit = event.submitter || form.querySelector('[type="submit"]');
    let formData = new FormData(form);
    if (form.hasAttribute('data-chrisps-bundle-form')) {
      // Frequently bought together: one request for every checked product.
      formData = new FormData();
      [...form.querySelectorAll('input[data-bundle-variant]:checked')].forEach((input, i) => {
        formData.append(`items[${i}][id]`, input.value);
        formData.append(`items[${i}][quantity]`, '1');
      });
      if (![...formData.keys()].length) return;
    }
    const errorEl = form.querySelector('[data-chrisps-form-error]') || document.getElementById(form.dataset.errorTarget || '');
    submit?.classList.add('is-loading');
    submit?.setAttribute('aria-busy', 'true');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    try {
      await Core.cart.add(formData, form);
      form.closest('dialog[data-chrisps-quick-shop-dialog]')?.close();
      if (submit) {
        submit.classList.add('is-added');
        setTimeout(() => submit.classList.remove('is-added'), 1600);
      }
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      } else {
        window.alert(error.message);
      }
    } finally {
      submit?.classList.remove('is-loading');
      submit?.removeAttribute('aria-busy');
    }
  });

  if (!customElements.get('chrisps-cart-drawer')) {
    customElements.define(
      'chrisps-cart-drawer',
      class extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
        }
        open(trigger) {
          Core.dialog.open(this.dialog, trigger);
        }
        close() {
          Core.dialog.close(this.dialog);
        }
      }
    );
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-chrisps-cart-toggle]');
    const drawer = document.querySelector('chrisps-cart-drawer');
    if (!toggle || !drawer || config.settings.cartType !== 'drawer') return;
    event.preventDefault();
    drawer.open(toggle);
  });

  /* Line items: quantity buttons, inputs and remove links (drawer + page) */
  if (!customElements.get('chrisps-cart-items')) {
    customElements.define(
      'chrisps-cart-items',
      class extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', (event) => {
            const button = event.target.closest('[data-chrisps-line-change]');
            if (!button) return;
            event.preventDefault();
            const line = Number(button.dataset.line);
            const input = this.querySelector(`input[data-line="${line}"]`);
            const qty = button.dataset.chrispsLineChange === 'remove' ? 0 : Math.max(0, Number(input?.value || 0) + Number(button.dataset.chrispsLineChange));
            this.update(line, qty);
          });
          this.addEventListener(
            'change',
            Core.debounce((event) => {
              const input = event.target.closest('input[data-line]');
              if (input) this.update(Number(input.dataset.line), Number(input.value));
            }, 300)
          );
        }
        async update(line, quantity) {
          this.setAttribute('aria-busy', 'true');
          const status = document.getElementById('chrisps-cart-status');
          try {
            await Core.cart.change(line, quantity, this);
            if (status) status.textContent = config.strings.cartUpdated;
          } catch (error) {
            if (status) status.textContent = error.message;
            await Core.cart.refresh();
          } finally {
            this.removeAttribute('aria-busy');
          }
        }
      }
    );
  }

  /* Discount codes (drawer + page) through the Cart AJAX API "discount"
     parameter. Codes can always be entered at checkout as well. */
  const currentCodes = (root) => [...root.querySelectorAll('[data-chrisps-discount-remove]')].map((b) => b.dataset.chrispsDiscountRemove);
  const applyCodes = async (codes, scope) => {
    const error = scope.querySelector('[data-chrisps-discount-error]');
    try {
      const cart = await Core.cart.update({ discount: codes.join(',') });
      await Core.cart.refresh();
      document.dispatchEvent(new CustomEvent('chrisps:cart:updated', { detail: { cart, source: scope, action: 'discount' } }));
    } catch (e) {
      if (error) {
        error.textContent = config.strings.discountError || e.message;
        error.hidden = false;
      }
    }
  };
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-chrisps-discount-form]');
    if (!form) return;
    event.preventDefault();
    const code = form.elements.discount.value.trim();
    if (!code) return;
    const scope = form.closest('details') || form;
    const codes = [...new Set([...currentCodes(scope), code])];
    form.querySelector('button')?.classList.add('is-loading');
    applyCodes(codes, scope);
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-chrisps-discount-remove]');
    if (!button) return;
    const scope = button.closest('details') || document;
    applyCodes(currentCodes(scope).filter((c) => c !== button.dataset.chrispsDiscountRemove), scope);
  });

  /* Order note and cart attributes such as a gift message (drawer + page) */
  document.addEventListener(
    'input',
    Core.debounce((event) => {
      const note = event.target.closest('[data-chrisps-cart-note]');
      if (note) Core.cart.update({ note: note.value });
      const attr = event.target.closest('[data-chrisps-cart-attribute]');
      if (attr) Core.cart.update({ attributes: { [attr.dataset.chrispsCartAttribute]: attr.value } });
    }, 500)
  );

  /* Free shipping progress — converts the store-currency threshold into the
     visitor's currency with Shopify's published rate. Display only. */
  if (!customElements.get('chrisps-free-shipping')) {
    customElements.define(
      'chrisps-free-shipping',
      class extends HTMLElement {
        connectedCallback() {
          this.render();
        }
        render() {
          // Thresholds are set in store currency; convert with Shopify's
          // published rate so international shoppers see correct amounts.
          const rate = Number(window.Shopify?.currency?.rate || 1);
          const total = Number(this.dataset.total);
          const milestones = (this.dataset.milestones || `${this.dataset.threshold}:`)
            .split('|')
            .map((m) => {
              const [amount, ...label] = m.split(':');
              return { amount: Math.round(Number(amount) * rate), label: label.join(':') };
            })
            .filter((m) => m.amount > 0);
          if (!milestones.length) return;
          const max = milestones[milestones.length - 1].amount;
          const next = milestones.find((m) => m.amount > total);
          const pct = Math.min(100, (total / max) * 100);
          const bar = this.querySelector('[data-progress]');
          if (bar) {
            bar.style.setProperty('--progress', `${pct}%`);
            bar.setAttribute('aria-valuenow', Math.round(pct));
          }
          this.querySelectorAll('[data-amount]').forEach((el) => el.classList.toggle('is-reached', total >= Number(el.dataset.amount) * rate));
          const text = this.querySelector('[data-message-text]') || this.querySelector('[data-message]');
          const remainingTpl = this.dataset.messageRemaining || config.strings.freeShippingRemaining || '';
          const reachedTpl = this.dataset.messageReached || config.strings.freeShippingReached || '';
          if (text) {
            text.textContent = next
              ? remainingTpl.replace('[amount]', Core.formatMoney(next.amount - total)).replace('[reward]', next.label)
              : reachedTpl.replace('[reward]', milestones[milestones.length - 1].label);
          }
          this.classList.toggle('is-complete', !next);
        }
      }
    );
  }

  /* Keep free shipping bars outside the drawer (e.g. product page) in sync */
  document.addEventListener('chrisps:cart:updated', async () => {
    const bars = document.querySelectorAll('chrisps-free-shipping');
    if (!bars.length) return;
    try {
      const cart = await (await fetch(`${config.routes.cart_url}.js`)).json();
      bars.forEach((bar) => {
        bar.dataset.total = cart.total_price;
        bar.render();
      });
    } catch (error) {
      /* non-critical */
    }
  });

  /* ----------------------------------------------------------------------
   * <chrisps-lazy-section data-url="…section_id=…">
   * Fetches server-rendered HTML when scrolled near (or immediately with
   * data-eager). Used for product recommendations, complementary products
   * and cart drawer suggestions — nothing loads until it is needed.
   * -------------------------------------------------------------------- */
  if (!customElements.get('chrisps-lazy-section')) {
    customElements.define(
      'chrisps-lazy-section',
      class extends HTMLElement {
        connectedCallback() {
          if (!this.dataset.url || this.loaded) return;
          if (this.hasAttribute('data-eager') || !('IntersectionObserver' in window)) {
            this.load();
            return;
          }
          this.io = new IntersectionObserver(
            (entries) => {
              if (entries.some((e) => e.isIntersecting)) {
                this.io.disconnect();
                this.load();
              }
            },
            { rootMargin: '400px 0px' }
          );
          // Hidden containers have no box and never intersect: watch their parent.
          const hiddenScope = this.closest('[hidden]');
          this.io.observe(hiddenScope?.parentElement || this);
        }
        disconnectedCallback() {
          this.io?.disconnect();
        }
        async load() {
          this.loaded = true;
          try {
            const doc = await Core.fetchHTML(this.dataset.url);
            const content = doc.querySelector('[data-chrisps-lazy-content]');
            if (content && content.children.length) {
              this.innerHTML = content.innerHTML;
              this.hidden = false;
              this.closest('[data-chrisps-hide-if-empty]')?.removeAttribute('hidden');
              Core.animate(this);
            } else if (this.hasAttribute('data-remove-empty')) {
              (this.closest('[data-chrisps-hide-if-empty]') || this).setAttribute('hidden', '');
            }
          } catch (error) {
            this.hidden = true;
          }
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * Quick shop (Section Rendering API — no duplicate product JSON)
   * -------------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-chrisps-quick-shop]');
    if (!trigger) return;
    event.preventDefault();
    const dialog = document.getElementById('ChrispsQuickShop');
    const body = dialog?.querySelector('[data-chrisps-quick-shop-body]');
    if (!dialog || !body) {
      window.location.href = trigger.dataset.chrispsQuickShop;
      return;
    }
    trigger.classList.add('is-loading');
    try {
      const doc = await Core.fetchHTML(`${trigger.dataset.chrispsQuickShop}?section_id=chrisps-quick-shop`);
      const content = doc.querySelector('[data-chrisps-quick-shop-content]');
      body.replaceChildren(...(content ? [content] : []));
      await Core.loadScript(config.routes.product_script);
      Core.animate(body);
      Core.dialog.open(dialog, trigger);
    } catch (error) {
      window.location.href = trigger.dataset.chrispsQuickShop;
    } finally {
      trigger.classList.remove('is-loading');
    }
  });

  /* ----------------------------------------------------------------------
   * Wishlist (browser storage, no app required)
   * -------------------------------------------------------------------- */
  const WISHLIST_KEY = 'chrisps:wishlist';
  Core.wishlist = {
    all: () => Core.storage.get(WISHLIST_KEY, []),
    has: (handle) => Core.wishlist.all().includes(handle),
    toggle(handle) {
      const list = Core.wishlist.all();
      const next = list.includes(handle) ? list.filter((h) => h !== handle) : [handle, ...list].slice(0, 100);
      Core.storage.set(WISHLIST_KEY, next);
      document.dispatchEvent(new CustomEvent('chrisps:wishlist:updated', { detail: { handles: next } }));
      return next.includes(handle);
    },
  };
  const renderWishlistCount = () => {
    const count = Core.wishlist.all().length;
    document.querySelectorAll('[data-chrisps-wishlist-count]').forEach((el) => {
      el.dataset.count = count;
      el.textContent = count || '';
    });
  };
  document.addEventListener('chrisps:wishlist:updated', renderWishlistCount);

  if (!customElements.get('chrisps-wishlist-button')) {
    customElements.define(
      'chrisps-wishlist-button',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.sync = this.sync.bind(this);
          this.button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            Core.wishlist.toggle(this.dataset.handle);
          });
          document.addEventListener('chrisps:wishlist:updated', this.sync);
          this.sync();
        }
        disconnectedCallback() {
          document.removeEventListener('chrisps:wishlist:updated', this.sync);
        }
        sync() {
          const active = Core.wishlist.has(this.dataset.handle);
          this.button.setAttribute('aria-pressed', String(active));
          this.button.setAttribute('aria-label', active ? this.button.dataset.labelRemove : this.button.dataset.labelAdd);
          this.classList.toggle('is-active', active);
          const text = this.querySelector('[data-wishlist-text]');
          if (text) text.textContent = active ? config.strings.wishlistSaved : config.strings.wishlistSave;
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * Recently viewed tracking (display lives in chrisps-product-lists.js)
   * -------------------------------------------------------------------- */
  Core.recentlyViewed = {
    key: 'chrisps:recently-viewed',
    all: () => Core.storage.get(Core.recentlyViewed.key, []),
    track(handle) {
      if (!config.settings.recentlyViewed || !handle) return;
      const list = [handle, ...Core.recentlyViewed.all().filter((h) => h !== handle)].slice(0, 12);
      Core.storage.set(Core.recentlyViewed.key, list);
    },
  };

  /* ----------------------------------------------------------------------
   * Countdown (announcement bars, banners, product blocks)
   * -------------------------------------------------------------------- */
  if (!customElements.get('chrisps-countdown')) {
    customElements.define(
      'chrisps-countdown',
      class extends HTMLElement {
        connectedCallback() {
          this.end = Number(this.dataset.end) * 1000;
          if (!this.end) return;
          this.parts = {
            days: this.querySelector('[data-days]'),
            hours: this.querySelector('[data-hours]'),
            minutes: this.querySelector('[data-minutes]'),
            seconds: this.querySelector('[data-seconds]'),
          };
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }
        disconnectedCallback() {
          clearInterval(this.timer);
        }
        tick() {
          const diff = Math.max(0, this.end - Date.now());
          const pad = (n) => String(n).padStart(2, '0');
          const d = Math.floor(diff / 86400000);
          const h = Math.floor((diff % 86400000) / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          const s = Math.floor((diff % 60000) / 1000);
          if (this.parts.days) this.parts.days.textContent = pad(d);
          if (this.parts.hours) this.parts.hours.textContent = pad(h);
          if (this.parts.minutes) this.parts.minutes.textContent = pad(m);
          if (this.parts.seconds) this.parts.seconds.textContent = pad(s);
          if (diff === 0) {
            clearInterval(this.timer);
            this.classList.add('is-complete');
            if (this.dataset.hideOnComplete === 'true' && !designMode) this.closest('[data-chrisps-countdown-scope]')?.setAttribute('hidden', '');
            const done = this.querySelector('[data-complete-message]');
            if (done) done.hidden = false;
          }
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * Announcement rotation (pauses on hover / focus, manual controls)
   * -------------------------------------------------------------------- */
  if (!customElements.get('chrisps-announcement')) {
    customElements.define(
      'chrisps-announcement',
      class extends HTMLElement {
        connectedCallback() {
          if (!this.hasAttribute('data-rotate')) return;
          this.index = 0;
          this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1));
          this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1));
          ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.stop()));
          ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.start()));
          this.go(0);
          this.classList.add('is-ready');
          this.start();
        }
        disconnectedCallback() {
          this.stop();
        }
        items() {
          return [...this.querySelectorAll('.chrisps-announcement__item')].filter((el) => !el.hidden);
        }
        go(step) {
          const items = this.items();
          if (!items.length) return;
          this.index = (this.index + step + items.length) % items.length;
          items.forEach((el, i) => {
            el.classList.toggle('is-active', i === this.index);
            el.toggleAttribute('inert', i !== this.index);
          });
        }
        start() {
          if (reducedMotion.matches || designMode) return;
          this.stop();
          this.timer = setInterval(() => this.go(1), Number(this.dataset.interval) || 6000);
        }
        stop() {
          clearInterval(this.timer);
        }
      }
    );
  }
  if (designMode) {
    // Mega menus: open the panel while its block is selected in the Theme Editor.
    document.addEventListener('shopify:block:select', (event) => {
      const menu = event.target.closest?.('details.chrisps-nav__details');
      if (menu) menu.open = true;
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const menu = event.target.closest?.('details.chrisps-nav__details');
      if (menu) menu.open = false;
    });
    document.addEventListener('shopify:block:select', (event) => {
      const bar = event.target.closest('chrisps-announcement');
      if (!bar || !bar.items) return;
      const i = bar.items().indexOf(event.target);
      if (i > -1) {
        bar.index = i;
        bar.go(0);
      }
    });
  }

  /* ----------------------------------------------------------------------
   * Header: sticky offset, disclosure menus, mobile drawer
   * -------------------------------------------------------------------- */
  if (!customElements.get('chrisps-header')) {
    customElements.define(
      'chrisps-header',
      class extends HTMLElement {
        connectedCallback() {
          const setHeight = () => document.documentElement.style.setProperty('--chrisps-header-height', `${this.offsetHeight}px`);
          setHeight();
          if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(this);
          if (this.dataset.sticky !== 'none') {
            const stuck = () => this.classList.toggle('is-stuck', window.scrollY > 4);
            stuck();
            window.addEventListener('scroll', stuck, { passive: true });
          }
          if (this.dataset.sticky === 'scroll-up') {
            let last = window.scrollY;
            window.addEventListener(
              'scroll',
              () => {
                const y = window.scrollY;
                this.classList.toggle('is-hidden', y > last && y > this.offsetHeight * 2);
                last = y;
              },
              { passive: true }
            );
          }
          if (this.dataset.transparent === 'true') {
            const base = [...this.classList].find((c) => c.startsWith('color-'));
            const scrolled = this.dataset.scrolledScheme;
            const toggle = () => {
              const on = window.scrollY > 10;
              this.classList.toggle('is-scrolled', on);
              if (base && scrolled && base !== scrolled) {
                this.classList.toggle(base, !on);
                this.classList.toggle(scrolled, on);
              }
            };
            toggle();
            window.addEventListener('scroll', toggle, { passive: true });
          }
        }
      }
    );
  }

  /* <details>-based menus: close on outside click, Escape, and when a
     sibling opens. Works without JS; JS adds hover intent (short open/close
     delays so menus never flicker) and dims the page behind mega menus. */
  if (!customElements.get('chrisps-disclosure-menu')) {
    customElements.define(
      'chrisps-disclosure-menu',
      class extends HTMLElement {
        connectedCallback() {
          this.details = [...this.querySelectorAll(':scope details')];
          const canHover = this.dataset.hover === 'true' && window.matchMedia('(hover: hover)').matches;
          this.details.forEach((d) => {
            d.addEventListener('toggle', () => {
              if (d.open) this.details.filter((o) => o !== d && !o.contains(d) && !d.contains(o)).forEach((o) => (o.open = false));
              this.syncState();
            });
            if (canHover) {
              d.addEventListener('mouseenter', () => {
                clearTimeout(d._closeTimer);
                const anyOpen = this.details.some((o) => o.open && o !== d);
                d._openTimer = setTimeout(() => (d.open = true), anyOpen ? 0 : 90);
              });
              d.addEventListener('mouseleave', () => {
                clearTimeout(d._openTimer);
                d._closeTimer = setTimeout(() => (d.open = false), 160);
              });
            }
          });
          this.onOutside = (event) => {
            if (!this.contains(event.target)) this.closeAll();
          };
          this.onKey = (event) => {
            if (event.key !== 'Escape') return;
            const open = this.details.filter((d) => d.open).pop();
            if (open) {
              open.open = false;
              open.querySelector('summary')?.focus();
            }
          };
          this.onFocusOut = (event) => {
            if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeAll();
          };
          document.addEventListener('click', this.onOutside);
          this.addEventListener('keydown', this.onKey);
          this.addEventListener('focusout', this.onFocusOut);
        }
        disconnectedCallback() {
          document.removeEventListener('click', this.onOutside);
          document.documentElement.classList.remove('chrisps-mega-open');
        }
        syncState() {
          const megaOpen = this.details.some((d) => d.open && d.classList.contains('chrisps-nav__details--mega'));
          document.documentElement.classList.toggle('chrisps-mega-open', megaOpen);
        }
        closeAll() {
          this.details.forEach((d) => (d.open = false));
        }
      }
    );
  }

  /* <chrisps-drill-menu>: mobile drill-down navigation. Top-level buttons
     slide in a sub-panel; Back returns focus to the opening button. */
  if (!customElements.get('chrisps-drill-menu')) {
    customElements.define(
      'chrisps-drill-menu',
      class extends HTMLElement {
        connectedCallback() {
          this.root = this.querySelector('[data-level="0"]');
          this.addEventListener('click', (event) => {
            const opener = event.target.closest('button[aria-controls]');
            const back = event.target.closest('[data-drill-back]');
            if (opener && this.root.contains(opener)) this.open(opener);
            if (back) this.back();
          });
          this.closest('dialog')?.addEventListener('close', () => this.reset());
        }
        open(opener) {
          const panel = this.querySelector(`#${CSS.escape(opener.getAttribute('aria-controls'))}`);
          if (!panel) return;
          this.opener = opener;
          opener.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          requestAnimationFrame(() => {
            this.root.classList.add('is-behind');
            panel.classList.add('is-active');
          });
          panel.querySelector('[data-drill-back]')?.focus({ preventScroll: true });
          this.scrollTop = 0;
        }
        back() {
          const panel = this.querySelector('.chrisps-drill__panel.is-active[data-level="1"]');
          if (!panel) return;
          panel.classList.remove('is-active');
          this.root.classList.remove('is-behind');
          const done = () => (panel.hidden = true);
          reducedMotion.matches ? done() : setTimeout(done, 350);
          this.opener?.setAttribute('aria-expanded', 'false');
          this.opener?.focus({ preventScroll: true });
        }
        reset() {
          this.querySelectorAll('.chrisps-drill__panel[data-level="1"]').forEach((p) => {
            p.classList.remove('is-active');
            p.hidden = true;
          });
          this.root.classList.remove('is-behind');
          this.querySelectorAll('[aria-expanded="true"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * Predictive search (Shopify Predictive Search API + section rendering)
   * -------------------------------------------------------------------- */
  if (!customElements.get('chrisps-predictive-search')) {
    customElements.define(
      'chrisps-predictive-search',
      class extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input[type="search"]');
          this.initRecent();
          this.results = this.querySelector('[data-chrisps-predictive-results]');
          this.status = this.querySelector('[data-chrisps-predictive-status]');
          if (!this.input || !this.results || !config.settings.predictiveSearch) return;
          this.controller = null;
          this.input.addEventListener('input', Core.debounce(() => this.search(), 250));
          this.input.addEventListener('keydown', (event) => this.onKeydown(event));
        }
        // Recent searches: kept on this device only (localStorage), max 6, newest first.
        initRecent() {
          const box = this.querySelector('[data-chrisps-recent-searches]');
          if (!box) return;
          const KEY = 'chrisps:recent-searches';
          const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } };
          const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ } };
          const render = () => {
            const list = read();
            const ul = box.querySelector('[data-chrisps-recent-list]');
            ul.replaceChildren(...list.map((q) => {
              const li = document.createElement('li');
              const a = document.createElement('a');
              a.className = 'chrisps-pill';
              a.href = `${config.routes.search_url}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
              a.textContent = q;
              li.append(a);
              return li;
            }));
            box.hidden = list.length === 0;
          };
          this.querySelector('form')?.addEventListener('submit', () => {
            const q = (this.input?.value || '').trim().slice(0, 80);
            if (q) write([q, ...read().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 6));
          });
          box.querySelector('[data-chrisps-recent-clear]')?.addEventListener('click', () => { write([]); render(); this.input?.focus(); });
          render();
        }
        async search() {
          const q = this.input.value.trim();
          if (q.length < 2) {
            this.results.innerHTML = '';
            this.input.setAttribute('aria-expanded', 'false');
            this.removeAttribute('data-has-results');
            return;
          }
          this.controller?.abort();
          this.controller = new AbortController();
          try {
            // Result types and product count come from Theme settings › Search.
            const types = this.dataset.types || 'product,query,collection,page,article';
            const limit = Math.min(10, Number(this.dataset.limit) || 6);
            const url = `${config.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=${encodeURIComponent(types)}&resources[limit]=${limit}&resources[limit_scope]=each&section_id=chrisps-predictive-search`;
            const doc = await Core.fetchHTML(url, { signal: this.controller.signal });
            const content = doc.querySelector('[data-chrisps-predictive-content]');
            this.results.innerHTML = content ? content.innerHTML : '';
            this.input.setAttribute('aria-expanded', 'true');
            this.toggleAttribute('data-has-results', Boolean(content));
            if (this.status) this.status.textContent = content?.dataset.statusText || '';
          } catch (error) {
            if (error.name !== 'AbortError') this.results.innerHTML = '';
          }
        }
        onKeydown(event) {
          const options = [...this.results.querySelectorAll('[role="option"]')];
          if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
          const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
          if (event.key === 'Enter') {
            if (current > -1) {
              event.preventDefault();
              options[current].querySelector('a')?.click();
            }
            return;
          }
          event.preventDefault();
          const next = event.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
          options.forEach((o, i) => o.setAttribute('aria-selected', String(i === next)));
          this.input.setAttribute('aria-activedescendant', options[next].id);
        }
      }
    );
  }

  /* Muted looping videos (Advert block, collection Video tiles): a pause
     control for WCAG 2.2.2, and no autoplay with reduced motion.
     Markup: [data-chrisps-video-scope] containing a <video> and an optional
     [data-chrisps-video-toggle] button. */
  const stillMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const settleVideos = (root = document) =>
    root.querySelectorAll('[data-chrisps-video-scope] video').forEach((video) => {
      if (!stillMotion) return;
      video.removeAttribute('autoplay');
      video.pause();
      video.closest('[data-chrisps-video-scope]')?.querySelector('[data-chrisps-video-toggle]')?.setAttribute('aria-pressed', 'true');
    });
  settleVideos();
  document.addEventListener('shopify:section:load', (event) => settleVideos(event.target));
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-chrisps-video-toggle]');
    const video = button?.closest('[data-chrisps-video-scope]')?.querySelector('video');
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
    button.setAttribute('aria-pressed', String(video.paused));
  });

  /* Share: native Web Share API, falling back to copying the link */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-chrisps-share]');
    if (!button) return;
    const data = { title: button.dataset.title, url: button.dataset.url };
    const status = button.nextElementSibling;
    try {
      if (navigator.share) await navigator.share(data);
      else {
        await navigator.clipboard.writeText(data.url);
        if (status) status.textContent = button.dataset.copied;
      }
    } catch (error) {
      /* share dismissed */
    }
  });

  /* ----------------------------------------------------------------------
   * Localisation selectors (country / language) auto-submit
   * -------------------------------------------------------------------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-chrisps-localization-select]');
    if (select) select.form?.submit();
  });

  /* ----------------------------------------------------------------------
   * Boot
   * -------------------------------------------------------------------- */
  const boot = () => {
    Core.applySchedules();
    Core.animate();
    renderWishlistCount();
    if (config.settings.productHandle) Core.recentlyViewed.track(config.settings.productHandle);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: re-initialise sections and blocks as they change */
  if (designMode) {
    ['shopify:section:load', 'shopify:block:select'].forEach((type) =>
      document.addEventListener(type, (event) => {
        Core.animate(event.target);
      })
    );
    document.addEventListener('shopify:section:select', (event) => {
      event.target.querySelectorAll('dialog[data-chrisps-editor-open]').forEach((d) => Core.dialog.open(d));
    });
  }

  Core.ready = true;
})();
